import { Dispatch, SetStateAction, useState } from 'react';

import { NATIVE_TOKEN_SYMBOL_PER_CHAIN } from '@/constants/chain';

import { autoBannerSelectors, useAppStore } from '@/store/app';
import { matchaAutoSelectors, useMarketTradeSettingsStore } from '@/store/tradeSettings/market';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { MatchaFlatGreenIcon } from '@/ui/Icons/MatchaFlatGreenIcon';
import { XIcon } from '@/ui/Icons/XIcon';

import { classNames } from '@/utils/classnames';

import animations from './animations.module.css';

interface MatchaAutoBannerProps {
  /**  Whether the banner is visible. */
  setAutoBannerVisible: Dispatch<SetStateAction<boolean>>;
  /** The chain ID of the current network. */
  chainId: number;
  className?: string;
}

const BANNER_ANIMATION_CLOSE_DURATION = 100;
const MONTHS_TO_HIDE = 1;

export const MatchaAutoBanner = ({
  chainId,
  className,
  setAutoBannerVisible,
}: MatchaAutoBannerProps) => {
  // state
  const { setAutoBannerDismissedUntil } = useAppStore(autoBannerSelectors);
  const { setIsMatchaAutoOn } = useMarketTradeSettingsStore(matchaAutoSelectors);
  const [isExiting, setIsExiting] = useState<boolean>(false);

  // vars
  const getDismissDate = () => {
    const now = new Date();
    return now.setMonth(now.getMonth() + MONTHS_TO_HIDE);
  };

  const dismiss = () => {
    setAutoBannerDismissedUntil(getDismissDate());
    setAutoBannerVisible(false);
  };

  // handlers
  const handleClickDismiss = () => {
    setIsExiting(true);
    setTimeout(() => dismiss(), BANNER_ANIMATION_CLOSE_DURATION);
  };

  const handleClickEnable = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsMatchaAutoOn(true);
      dismiss();
    }, BANNER_ANIMATION_CLOSE_DURATION);
  };

  return (
    <div
      className={classNames(
        'bg-special-background-modal shadow-30 rounded-xl pt-3 pb-5 px-6 overflow-hidden',
        isExiting ? animations['animate-popover-out'] : animations['animate-popover-in'],
        className,
      )}
    >
      <div className="flex items-center justify-between w-full h-9">
        <h3
          className={fontStyles({ variant: 'LABEL_LG', class: ['flex', 'items-center', 'gap-2'] })}
        >
          <MatchaFlatGreenIcon aria-hidden />
          No {NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId]}? No problem!
        </h3>

        <button
          aria-label="Dismiss"
          className={buttons({
            size: 'small',
            color: 'transparent',
            shape: 'rounded',
            class: ['-mr-3'],
          })}
          onClick={handleClickDismiss}
        >
          <XIcon className="size-4" />
        </button>
      </div>

      <p className={fontStyles({ variant: 'BODY_MD', class: ['mt-2', 'text-token-gray-1000'] })}>
        Trade without holding native tokens. Matcha Auto will handle the network cost for you.
      </p>

      <button
        className={buttons({
          size: 'small',
          color: 'alphaGray',
          shape: 'rounded',
          class: ['mt-4'],
        })}
        onClick={handleClickEnable}
      >
        Enable Matcha Auto
      </button>
    </div>
  );
};
